/**
 * Error tracking and log delivery — US 21.04.
 *
 * Lives in transport/ because it talks to an external service; the pure event
 * shape it delivers is `shared/log.ts`. Everything is gated on a DSN being
 * configured: without one, every call is a cheap no-op and local dev stays
 * silent. Errors are how we learn the game is broken before players tell us.
 *
 * Note: Vite replaces `import.meta.env.VITE_SENTRY_DSN` with a literal at
 * build time, so a DSN-less build tree-shakes the entire Sentry SDK out of
 * the bundle (verified: 0.8 kB vs 28.4 kB gzipped). The corollary: the DSN
 * cannot be supplied at runtime — it is baked in by the deploy pipeline.
 */
import * as Sentry from '@sentry/browser';
import type { LogEvent } from '@shared/log.js';
import { makeLogEvent, serialiseLogEvent } from '@shared/log.js';
import type { LogEventInput } from '@shared/log.js';

let enabled = false;

export function initObservability(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_APP_ENV ?? 'development';
  if (!dsn) return; // local dev, or the tracker is deliberately off

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_RELEASE,
    // Economy correctness matters more than performance traces; keep the free
    // tier for errors. Revisit when US 21.05 models costs.
    tracesSampleRate: 0,
    sendDefaultPii: false,
  });
  enabled = true;
}

/**
 * Reports an exception with its correlating actionId. The actionId is what
 * connects a client stack trace to the server logs for the same request.
 */
export function reportError(error: unknown, context?: { actionId?: string; msg?: string }): void {
  if (!enabled) {
    console.error(context?.msg ?? 'unhandled error', error, context?.actionId ?? '');
    return;
  }
  Sentry.withScope((scope) => {
    if (context?.actionId) scope.setTag('actionId', context.actionId);
    if (context?.msg) scope.setContext('detail', { msg: context.msg });
    Sentry.captureException(error);
  });
}

/**
 * Emits a structured log event: console line always, Sentry breadcrumb when
 * enabled so the events leading up to a crash arrive with its report.
 */
export function log(input: LogEventInput): LogEvent {
  const event = makeLogEvent(input);
  const line = serialiseLogEvent(event);
  if (event.level === 'error') console.error(line);
  else if (event.level === 'warn') console.warn(line);
  // debug/info stay off the console — they exist for breadcrumbs.

  if (enabled) {
    Sentry.addBreadcrumb({
      category: 'game',
      level: event.level === 'warn' ? 'warning' : event.level,
      message: event.msg,
      data: { actionId: event.actionId, ...event.ctx },
    });
  }
  return event;
}
